import React, {useContext, useEffect, useRef, useState} from "react";
import {Form, Select} from "antd";
import {ContentContext} from "@context/ContentContext";
import axios from "./axios";
import {BrandContext} from "@context/BrandContext";
import MathJaxHTMLContent from "./MathJaxHTMLContent";

export const SELECT_LEVELS = {
    SYLLABUS_GROUP: 'syllabusGroup',
    SYLLABUS: 'syllabus',
    TOPIC: 'topic',
    CONCEPT: 'concept',
    NONE: 'none',
}

const syllabusTopicConceptModel = {
    syllabusGroupId: null,
    syllabusId: null,
    topicId: null,
    conceptId: null,
    activeObject: null,
}

// Helper: Ensure ID is valid and NOT zero (0 means 'All')
const isValidAndNotZero = (id) => id !== null && id !== undefined && id !== '' && !isNaN(Number(id)) && Number(id) !== 0;

export const getUserSyllabusGroups = (params)=> {
    return axios.get('/web/ui/content/findUserSyllabusGroups', { params })
        .then(response => response.data || [])
        .catch(error => {
            console.error('Error retrieving syllabus groups: ', error);
            return [];
        });
}

export const getSyllabusesByUserBrandGroup = (params)=> {
    return axios.get('/web/ui/content/findSyllabusesByUserBrandGroup', { params })
        .then(response => response.data || [])
        .catch(error => {
            console.error('Error retrieving syllabuses: ', error);
            return [];
        });
}

export const getUserTopics = (params)=> {
    return axios.get('/web/ui/content/findTopicsForSyllabus', { params })
        .then(response => response.data || [])
        .catch(error => {
            console.error('Error retrieving topics: ', error);
            return [];
        });
}

export const getUserConcepts = (params)=> {
    return axios.get('/web/ui/content/findConceptsForTopic', { params })
        .then(response => response.data || [])
        .catch(error => {
            console.error('Error retrieving concepts: ', error);
            return [];
        });
}

const GenericSyllabusTopicConceptSelect = props => {
    const {
        form,
        selectLevel = SELECT_LEVELS.CONCEPT,
        defaultLevel = SELECT_LEVELS.CONCEPT,
        initialIds,

        // Legacy/Custom URL Props (Ignored by ContentController but kept for compatibility)
        resourceType,
        questionClusterTypes,
        autoMark,
        questionType,
        mediaType,
        customTopicsConceptsUrl,

        disabled = false,
        onLoadComplete,
        onChange,

        // ContentController Endpoints Props
        forTests,
        forExams,
        hasQuestions,
        hasScorcher,
        forReports,
        includeSupplementary,
        hasCurriculumLink,
        syllabusType
    } = props;

    const brandConfig = useContext(BrandContext);
    const {content} = useContext(ContentContext);
    const [loading, setLoading] = useState(true);
    const [idsState, setIdsState] = useState(null);
    const [topicsConceptList, setTopicsConceptList] = useState([]);
    const [syllabusList, setSyllabusList] = useState([]);

    const [syllabusGroupList, setSyllabusGroupList] = useState([]);
    const [fetchingGroups, setFetchingGroups] = useState(true);

    const initializedRef = useRef(false);
    const syllabusGroupRef = useRef(null),
        syllabusRef = useRef(null),
        lastKeyPressed = useRef(null),
        lastRef = useRef(null);

    useEffect(() => {
        setFetchingGroups(true);
        getUserSyllabusGroups({ forExams, includeSupplementary }).then(groups => {
            setSyllabusGroupList(groups || []);
            setFetchingGroups(false);
        });
    }, [forExams, includeSupplementary]);

    useEffect(() => {
        if (!content || fetchingGroups || initializedRef.current) return;
        initializedRef.current = true;

        let newSyllabusGroupId = initialIds?.syllabusGroupId ?? content?.currentSyllabusGroup?.syllabusGroupId;

        if (initialIds?.syllabusId) {
            const syllabusGroup = syllabusGroupList?.find(syllabusGroup =>
                syllabusGroup?.syllabuses?.some(syllabus => syllabus.syllabusId === initialIds?.syllabusId)
            );
            if (syllabusGroup) newSyllabusGroupId = syllabusGroup.syllabusGroupId;
        }

        updateSelects({...initialIds, syllabusGroupId: newSyllabusGroupId, isInitialization: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, initialIds, fetchingGroups, syllabusGroupList]);

    const updateSelects = async ({syllabusGroupId: updateSyllabusGroupId, syllabusId: updateSyllabusId, topicId: updateTopicId, conceptId: updateConceptId, isInitialization = false}) => {
        const findNextAvailableId = ({items = [], idKey, selectId, selectName, userCurrentId, defaultLevels}) => {
            if (selectId != null) {
                return selectId;
            } else if (!defaultLevels.includes(defaultLevel)) {
                return 0; // Value for 'All'
            } else if (items?.some(item => !item.disabled && item[idKey] === userCurrentId)) {
                return userCurrentId;
            } else {
                const firstAvailable = items?.find(item => !item?.disabled);
                return firstAvailable ? firstAvailable[idKey] : `No available ${selectName}`;
            }
        };

        const validateField = ({selectId, fieldValueObj, defaultLevels}) => {
            let error = false;
            if (!defaultLevels.includes(defaultLevel) && !fieldValueObj) {
                error = true;
            }
            return [{
                name: selectId,
                errors: (error || fieldValueObj?.disabled) ? [" "] : [],
            }];
        }

        const getActiveSelectObject = (currentIds, currentTopicsConceptList, currentSyllabusList) => {
            if (selectLevel === SELECT_LEVELS.SYLLABUS_GROUP) {
                return syllabusGroupList?.find(sg => sg.syllabusGroupId === currentIds.syllabusGroupId);
            } else if (selectLevel === SELECT_LEVELS.SYLLABUS) {
                return currentSyllabusList?.find(s => s.syllabusId === currentIds.syllabusId);
            } else if (selectLevel === SELECT_LEVELS.TOPIC) {
                return currentTopicsConceptList?.find(t => t.topicId === currentIds.topicId);
            } else {
                return currentTopicsConceptList?.find(t => t.topicId === currentIds.topicId)?.concepts?.find(c => c.conceptId === currentIds.conceptId);
            }
        }

        const newIdsState = {...(idsState || syllabusTopicConceptModel)};
        let newSyllabusList = syllabusList || [];
        let newTopicsConceptList = topicsConceptList || [];

        try {
            if (
                updateSyllabusGroupId === idsState?.syllabusGroupId &&
                updateSyllabusId === idsState?.syllabusId &&
                updateTopicId === idsState?.topicId &&
                updateConceptId === idsState?.conceptId
            ) return;

            if (updateSyllabusGroupId != null) newIdsState.syllabusGroupId = updateSyllabusGroupId;

            // Update Syllabuses
            if (updateSyllabusGroupId != null || updateSyllabusId != null) {
                if (updateSyllabusGroupId != null && updateSyllabusGroupId !== idsState?.syllabusGroupId) {
                    setLoading(true);
                    if (isValidAndNotZero(newIdsState.syllabusGroupId)) {
                        newSyllabusList = await getSyllabusesByUserBrandGroup({
                            syllabusGroupId: newIdsState.syllabusGroupId,
                            syllabusType,
                            includeSupplementary,
                            hasCurriculumLink
                        }) || [];
                    } else {
                        newSyllabusList = [];
                    }
                    setSyllabusList(newSyllabusList);
                }

                newIdsState.syllabusId = findNextAvailableId({
                    items: newSyllabusList,
                    idKey: 'syllabusId',
                    selectId: updateSyllabusId,
                    selectName: brandConfig.syllabusesName,
                    userCurrentId: content?.currentSyllabus?.syllabusId,
                    defaultLevels: [SELECT_LEVELS.SYLLABUS, SELECT_LEVELS.TOPIC, SELECT_LEVELS.CONCEPT]
                });
            }

            // Update Topics
            if (updateSyllabusGroupId != null || updateSyllabusId != null || updateTopicId != null) {
                setLoading(true);

                if (isValidAndNotZero(newIdsState.syllabusId)) {
                    let params = {
                        syllabusId: newIdsState.syllabusId,
                        forTests, forExams, hasQuestions, hasScorcher, forReports
                    };

                    if (customTopicsConceptsUrl) {
                        const response = await axios.get(customTopicsConceptsUrl, {params});
                        newTopicsConceptList = response.data || [];
                    } else {
                        // Legacy payload mapping
                        const legacyParams = { resourceType, questionClusterTypes, autoMark, questionType, mediaType };
                        Object.keys(legacyParams).forEach(key => legacyParams[key] != null && (params[key] = legacyParams[key]));

                        newTopicsConceptList = await getUserTopics(params) || [];
                    }
                } else {
                    newTopicsConceptList = [];
                }

                setTopicsConceptList(newTopicsConceptList);
                newIdsState.topicId = findNextAvailableId({
                    items: newTopicsConceptList,
                    idKey: 'topicId',
                    selectId: updateTopicId,
                    selectName: brandConfig.topicsName,
                    userCurrentId: content?.currentTopic?.topicId,
                    defaultLevels: [SELECT_LEVELS.TOPIC, SELECT_LEVELS.CONCEPT]
                });
            }

            // Update Concepts
            if (updateSyllabusGroupId != null || updateSyllabusId != null || updateTopicId != null || updateConceptId != null) {
                let topic = newTopicsConceptList?.find(t => t.topicId === newIdsState.topicId);

                if (topic && !topic.concepts && selectLevel === SELECT_LEVELS.CONCEPT) {
                    if (isValidAndNotZero(newIdsState.topicId)) {
                        setLoading(true);
                        let params = { topicId: newIdsState.topicId, forTests, hasQuestions, forReports, hasScorcher };

                        // Legacy payload mapping
                        const legacyParams = { resourceType, questionClusterTypes, autoMark, questionType, mediaType };
                        Object.keys(legacyParams).forEach(key => legacyParams[key] != null && (params[key] = legacyParams[key]));

                        topic.concepts = await getUserConcepts(params) || [];
                    } else {
                        topic.concepts = [];
                    }
                    setTopicsConceptList([...newTopicsConceptList]);
                }

                newIdsState.conceptId = findNextAvailableId({
                    items: topic?.concepts || [],
                    idKey: 'conceptId',
                    selectId: updateConceptId,
                    selectName: brandConfig.conceptsName,
                    userCurrentId: content?.currentConcept?.conceptId,
                    defaultLevels: [SELECT_LEVELS.CONCEPT]
                });
            }

            // Update Form state securely
            if (form) {
                // FIXED: AntD v4+ setFieldsValue automatically merges state.
                // Do NOT spread form.getFieldsValue as it is a function.
                form.setFieldsValue(newIdsState);

                form.setFields([
                    ...validateField({
                        selectId: "syllabusGroupId",
                        fieldValueObj: syllabusGroupList?.find(sg => sg.syllabusGroupId === newIdsState.syllabusGroupId),
                        defaultLevels: []
                    }),
                    ...validateField({
                        selectId: "syllabusId",
                        fieldValueObj: newSyllabusList?.find(s => s.syllabusId === newIdsState.syllabusId),
                        defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP]
                    }),
                    ...validateField({
                        selectId: "topicId",
                        fieldValueObj: newTopicsConceptList?.find(t => t.topicId === newIdsState.topicId),
                        defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP, SELECT_LEVELS.SYLLABUS]
                    }),
                    ...validateField({
                        selectId: "conceptId",
                        fieldValueObj: newTopicsConceptList?.find(t => t.topicId === newIdsState.topicId)?.concepts?.find(c => c.conceptId === newIdsState.conceptId),
                        defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP, SELECT_LEVELS.SYLLABUS, SELECT_LEVELS.TOPIC]
                    })
                ]);
            }

            newIdsState.activeObject = getActiveSelectObject(newIdsState, newTopicsConceptList, newSyllabusList);
            setIdsState(newIdsState);

            if (isInitialization && onLoadComplete && typeof onLoadComplete === "function") {
                onLoadComplete(newIdsState.activeObject);
            } else if (!isInitialization && onChange && typeof onChange === "function") {
                onChange(newIdsState.activeObject);
            }
        } catch (error) {
            console.error("Failed to update selects", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{
        if (!loading && lastKeyPressed.current === 'Enter') {
            lastRef.current?.focus();
        }
    },[loading]);

    return (
        <>
            <Form.Item label={brandConfig.syllabusGroupName} name="syllabusGroupId">
                <Select
                    ref={syllabusGroupRef}
                    onKeyDownCapture={(e) => { lastKeyPressed.current = e.code; }}
                    onClick={()=>{lastKeyPressed.current = null;}}
                    onSelect={()=>{lastRef.current = syllabusGroupRef.current;}}
                    onChange={syllabusGroupId => updateSelects({syllabusGroupId})}
                    virtual={false}
                    showSearch={false}
                    loading={loading || fetchingGroups}
                    disabled={disabled || loading || fetchingGroups || (form.getFieldError("syllabusGroupId")?.length > 0)}
                    getPopupContainer={trigger => trigger.parentElement}
                >
                    {defaultLevel === SELECT_LEVELS.NONE && <Select.Option value={0}>All</Select.Option>}
                    {syllabusGroupList?.map(syllabusGroup => (
                        <Select.Option key={syllabusGroup.syllabusGroupId} value={syllabusGroup.syllabusGroupId}>{syllabusGroup.syllabusGroupName}</Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {(selectLevel === SELECT_LEVELS.SYLLABUS || selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item label={brandConfig.syllabusName} name="syllabusId">
                    <Select
                        ref={syllabusRef}
                        onKeyDownCapture={(e) => { lastKeyPressed.current = e.code; }}
                        onClick={()=>{lastKeyPressed.current = null;}}
                        onSelect={()=>{lastRef.current = syllabusRef.current;}}
                        onChange={syllabusId => updateSelects({syllabusId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("syllabusId")?.length > 0)}
                        getPopupContainer={trigger => trigger.parentElement}
                    >
                        {defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP && <Select.Option value={0}>All</Select.Option>}
                        {syllabusList?.map(syllabus => (
                            <Select.Option key={syllabus.syllabusId} value={syllabus.syllabusId}>{syllabus.syllabusName}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            {(selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item label={brandConfig.topicName} name="topicId">
                    <Select
                        onChange={topicId => updateSelects({topicId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("topicId")?.length > 0)}
                        getPopupContainer={trigger => trigger.parentElement}
                    >
                        {(defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) && <Select.Option value={0}>All</Select.Option>}
                        {topicsConceptList?.map(topic => (
                            <Select.Option key={topic.topicId} value={topic.topicId} disabled={topic.disabled}>{topic.topicName}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            {selectLevel === SELECT_LEVELS.CONCEPT && (
                <Form.Item label={brandConfig.conceptName} name="conceptId">
                    <Select
                        onChange={conceptId => updateSelects({conceptId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("conceptId")?.length > 0)}
                        getPopupContainer={trigger => trigger.parentElement}
                    >
                        {(defaultLevel === SELECT_LEVELS.TOPIC || defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) && <Select.Option value={0}>All</Select.Option>}
                        {topicsConceptList?.find(topic => topic.topicId === idsState?.topicId)?.concepts?.map(concept => (
                            <Select.Option key={concept.conceptId} value={concept.conceptId} disabled={concept.disabled}>
                                <MathJaxHTMLContent html={concept.conceptName}/>
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
        </>
    )
}

export default React.memo(GenericSyllabusTopicConceptSelect);